import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PhoneCall, ShieldAlert, CheckCircle2, XCircle, Sparkles, Calendar } from "lucide-react";

const TYPE_META = (s) => ({
  advisor_call: {
    label: `Day ${s?.comeback_day_60_days ?? 60} · Advisor Call`,
    Icon: PhoneCall,
    tint: "border-l-sky-500 bg-sky-50/40 dark:bg-sky-950/10",
    accent: "bg-sky-500 text-white",
    testid: "comeback-tab-advisor",
  },
  manager_escalation: {
    label: `Day ${s?.comeback_day_90_days ?? 90} · Manager Escalation`,
    Icon: ShieldAlert,
    tint: "border-l-rose-500 bg-rose-50/40 dark:bg-rose-950/10",
    accent: "bg-rose-500 text-white",
    testid: "comeback-tab-manager",
  },
});

function TaskCard({ task, onComplete, onDismiss, settings }) {
  const meta = TYPE_META(settings)[task.task_type] || TYPE_META(settings).advisor_call;
  const { Icon } = meta;
  return (
    <Card
      className={`p-4 border-border border-l-4 ${meta.tint}`}
      data-testid={`comeback-task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md ${meta.accent} grid place-items-center shrink-0`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-display font-bold text-base truncate">
              {task.customer_name}
            </div>
            <div className="text-xs text-muted-foreground truncate">{task.vehicle}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Declined{" "}
            <span className="font-semibold text-foreground">
              {(task.service_items || []).map((s) => s.name).join(", ") || "recommended service"}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge variant="outline" className="font-mono-label text-[0.6rem]">
              <Calendar className="w-3 h-3 mr-1" />
              {task.days_since_decline} days since decline
            </Badge>
            <span className="text-[0.7rem] text-muted-foreground">
              Assigned to{" "}
              <span className="font-semibold text-foreground">{task.assigned_to_name}</span>
            </span>
            <a
              href={`tel:${task.customer_phone}`}
              className="text-[0.7rem] font-mono text-primary hover:underline"
              data-testid={`comeback-call-${task.id}`}
            >
              {task.customer_phone}
            </a>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button
            size="sm"
            onClick={() => onComplete(task, "booked")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
            data-testid={`comeback-booked-${task.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
            Booked
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onComplete(task, "reached")}
            className="h-8"
            data-testid={`comeback-reached-${task.id}`}
          >
            Called
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDismiss(task)}
            className="h-8 text-muted-foreground hover:text-red-600"
            data-testid={`comeback-dismiss-${task.id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
            Dismiss
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function ComebacksPage() {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("all"); // all | advisor_call | manager_escalation
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [pendingTask, setPendingTask] = useState(null);
  const [pendingOutcome, setPendingOutcome] = useState("reached");
  const [noteText, setNoteText] = useState("");

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/comebacks", { params: { task_type: filter } }),
        api.get("/comebacks/summary"),
      ]);
      setTasks(t.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not load comebacks");
    }
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedTasks = useMemo(() => {
    const byType = { advisor_call: [], manager_escalation: [] };
    tasks.forEach((t) => {
      if (byType[t.task_type]) byType[t.task_type].push(t);
    });
    return byType;
  }, [tasks]);

  const openCompleteDialog = (task, outcome) => {
    setPendingTask(task);
    setPendingOutcome(outcome);
    setNoteText("");
    setNoteOpen(true);
  };

  const submitComplete = async () => {
    if (!pendingTask) return;
    setBusy(true);
    try {
      await api.patch(`/comebacks/${pendingTask.id}/complete`, {
        outcome: pendingOutcome,
        notes: noteText.trim() || null,
      });
      toast.success(
        pendingOutcome === "booked"
          ? `${pendingTask.customer_name} booked — nice recovery`
          : `Logged your call to ${pendingTask.customer_name}`
      );
      setNoteOpen(false);
      setPendingTask(null);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const dismissTask = async (task) => {
    if (!window.confirm(`Dismiss the comeback for ${task.customer_name}?`)) return;
    setBusy(true);
    try {
      await api.patch(`/comebacks/${task.id}/dismiss`);
      toast.success(`Dismissed`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const seedDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/comebacks/seed-demo");
      toast.success(
        `Seeded — ${data.comeback_30_sent} final-SMS texts, ${data.comeback_60_tasks} advisor tasks, ${data.comeback_90_tasks} manager escalations`
      );
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not seed demo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardShell>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Comeback Protocol</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            30 · 60 · 90 Day Recovery
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            After two texts don&apos;t convert, we don&apos;t quit. Day{" "}
            {settings?.comeback_day_30_days ?? 30} fires a final SMS automatically. Day{" "}
            {settings?.comeback_day_60_days ?? 60} drops an advisor call task here. Day{" "}
            {settings?.comeback_day_90_days ?? 90} escalates to your Service Manager for a relationship call.
          </p>
        </div>
        <Button
          onClick={seedDemo}
          disabled={busy}
          variant="outline"
          className="border-[hsl(var(--gold))] text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10"
          data-testid="comeback-seed-demo-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Seed Demo Data
        </Button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-l-4 border-l-sky-500" data-testid="comeback-kpi-advisor">
          <div className="flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-sky-600" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">Day {settings?.comeback_day_60_days ?? 60} · Advisor Calls</div>
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums">
            {summary?.advisor_pending ?? "—"}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-1">pending calls</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-rose-500" data-testid="comeback-kpi-manager">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-600" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">Day {settings?.comeback_day_90_days ?? 90} · Manager Escalations</div>
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums">
            {summary?.manager_pending ?? "—"}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-1">need manager touch</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="comeback-kpi-completed">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">Completed This Month</div>
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums">
            {summary?.completed_this_month ?? "—"}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-1">tasks worked</div>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { k: "all", l: "All Pending" },
          { k: "advisor_call", l: `Advisor Calls (Day ${settings?.comeback_day_60_days ?? 60})` },
          { k: "manager_escalation", l: `Manager Escalations (Day ${settings?.comeback_day_90_days ?? 90})` },
        ].map(({ k, l }) => (
          <Button
            key={k}
            size="sm"
            variant={filter === k ? "default" : "outline"}
            onClick={() => setFilter(k)}
            data-testid={`comeback-filter-${k}`}
          >
            {l}
          </Button>
        ))}
      </div>

      {tasks.length === 0 ? (
        <Card className="p-12 text-center" data-testid="comeback-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" strokeWidth={1.5} />
          <div className="font-display font-bold text-lg">Inbox zero</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            No pending comebacks. Everyone is either scheduled or worked. Nice job.
            When declines age past 60 or 90 days without booking, they'll show up here.
          </p>
        </Card>
      ) : filter === "all" ? (
        <div className="space-y-6">
          {Object.entries(groupedTasks).map(([type, list]) => {
            if (list.length === 0) return null;
            const meta = TYPE_META(settings)[type];
            return (
              <div key={type} data-testid={`comeback-group-${type}`}>
                <div className="flex items-center gap-2 mb-2">
                  <meta.Icon className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
                  <div className="font-mono-label text-muted-foreground">
                    {meta.label} · {list.length}
                  </div>
                </div>
                <div className="space-y-2">
                  {list.map((t) => (
                    <TaskCard key={t.id} task={t} onComplete={openCompleteDialog} onDismiss={dismissTask} settings={settings} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} onComplete={openCompleteDialog} onDismiss={dismissTask} settings={settings} />
          ))}
        </div>
      )}

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent data-testid="comeback-note-dialog">
          <DialogHeader>
            <DialogTitle>
              {pendingOutcome === "booked" ? "Mark as booked" : "Log this call"}
            </DialogTitle>
            <DialogDescription>
              {pendingTask?.customer_name} · {pendingTask?.vehicle}
              {pendingOutcome === "booked"
                ? " — nice recovery. Add a note if you want to remember what closed it."
                : " — what happened? (Optional note the next person picking this up can see.)"}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Optional note…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={4}
            data-testid="comeback-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitComplete}
              disabled={busy}
              className={pendingOutcome === "booked" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
              data-testid="comeback-note-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
